import React from 'react';
import _ from 'lodash';
import { Button, Box, Checkbox, Stack, Typography, List } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitTransportAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { FilterTree } from '../../promotion/promotion-details/FilterTree';

export const TransportForm = props => {
  const {
    data,
    submitCallback,
    handleClose,
    allResorts,
    allSeasons,
    treeObjects,
    seasonChangeCallback
  } = props;
  const treeRef = React.useRef();

  const { loading, mutate } = useMutation(submitTransportAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    gross_value: Yup.number().required('To pole nie może być puste'),
    gross_value_one_way: Yup.number().required('To pole nie może być puste'),
    active: Yup.boolean(),
    internal: Yup.boolean()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    gross_value: !_.isNil(data) ? data.gross_value : '',
    gross_value_one_way: !_.isNil(data) ? data.gross_value_one_way : '',
    resorts: data ? data.resorts : [],
    season: data
      ? data.season || allSeasons[allSeasons.length - 1].value
      : allSeasons[allSeasons.length - 1].value,
    active: data ? data.active || false : false,
    internal: data ? data.internal || false : false,
    ignore_in_transport_controller: data
      ? data.ignore_in_transport_controller || false
      : false
  };

  const handleSubmit = async values => {
    values.tree = treeRef.current.getTree();
    const { error, payload } = await mutate(
      values,
      data ? 'PUT' : 'POST',
      data ? data.id : undefined
    );
    if (!error) {
      submitCallback(
        payload,
        data ? 'edit' : 'created',
        'success',
        data ? 'Zaktualizowano transport' : 'Dodano nowy transport',
        ''
      );
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego transportu');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(
        data,
        'delete',
        'success',
        `Usunięto transport ${data.name}`,
        ''
      );
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć transportu ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const seasonChange = val => {
    formik.setFieldValue('season', val);
    seasonChangeCallback(val);
  };

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="row" justifyContent="center">
          <Box
            display="flex"
            flexDirection="column"
            mt={1}
            p={1}
            ml="5%"
            mr="5%"
            flex="1 1 60%"
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Nazwa
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="name"
                id="name"
                type="text"
                placeholder="nazwa transportu"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                debounceTimeout={100}
                element={SmallFormTextField}
                autoFocus
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Cena brutto (zł)
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="gross_value"
                id="gross_value"
                type="text"
                placeholder="cena transportu"
                value={formik.values.gross_value}
                onChange={formik.handleChange}
                error={
                  formik.touched.gross_value &&
                  Boolean(formik.errors.gross_value)
                }
                helperText={
                  formik.touched.gross_value && formik.errors.gross_value
                }
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Cena brutto za transport w 1 stronę (zł)
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="gross_value_one_way"
                id="gross_value_one_way"
                type="text"
                placeholder="cena transportu w 1 stronę"
                value={formik.values.gross_value_one_way}
                onChange={formik.handleChange}
                error={
                  formik.touched.gross_value_one_way &&
                  Boolean(formik.errors.gross_value_one_way)
                }
                helperText={
                  formik.touched.gross_value_one_way &&
                  formik.errors.gross_value_one_way
                }
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Ośrodki
              </Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 90%' }}
                select
                multiple
                id="resorts"
                name="resorts"
                options={allResorts}
                value={formik.values.resorts}
                onChange={formik.handleChange}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Sezon
              </Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 90%' }}
                select
                id="season"
                name="season"
                options={allSeasons}
                value={formik.values.season}
                onChange={e => seasonChange(e.target.value)}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Jest aktywna
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="active"
                name="active"
                checked={formik.values.active}
                onChange={formik.handleChange}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Wewnętrzny
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="internal"
                name="internal"
                checked={formik.values.internal}
                onChange={formik.handleChange}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 10%">
                Ignoruj w kontrolerze miejsc
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="ignore_in_transport_controller"
                name="ignore_in_transport_controller"
                checked={formik.values.ignore_in_transport_controller}
                onChange={formik.handleChange}
              />
            </Stack>
          </Box>
          <Box flex="1 1 20%">
            <List disablePadding>
              {!_.isEmpty(treeObjects) ? (
                <FilterTree
                  data={treeObjects}
                  ref={treeRef}
                  initSelected={data ? data.init_selected_tree : []}
                />
              ) : null}
            </List>
          </Box>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        {data ? (
          <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
            Usuń
          </Button>
        ) : null}
      </form>
      <ConfirmDialog
        title="Usunąć transport?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć ten transport?
      </ConfirmDialog>
    </>
  );
};
